/**
 * iter 71.2 — Cases drawer now uses the shared `useDrawerForm` + `DrawerFooter`
 * primitives (the same lifecycle behind UserManagerDrawer / LocationManagerDrawer).
 *
 *   - Dirty-guarded close (backdrop / Esc / Cancel prompts before discarding
 *     unsaved edits).
 *   - Sticky right-aligned footer with uniform CANCEL / CREATE-or-SAVE buttons.
 *   - <ErrorBanner /> for consistent inline error rendering.
 */
import { useEffect } from "react";
import { useState } from "react";
import { api, formatErr } from "../lib/api";
import { Drawer, Field } from "../components/UI";
import { Page } from "../components/AppShell";
import { Plus, Scales } from "@phosphor-icons/react";
import useBulkSelect from "../lib/useBulkSelect";
import {
  useDrawerForm, DrawerFooter, ErrorBanner,
} from "../components/OrgAdmin/_managerShared";

const EMPTY = {
  case_number: "",
  case_type_id: "",
  court_location_id: "",
  plaintiff: "",
  defendant: "",
  in_re_case: false,
  in_re_case_name: "",
  judge: "",
  date_filed: "",      // YYYY-MM-DD; submitted as ISO at save time.
  case_synopsis: "",
};

const hydrate = (c) => ({
  case_number: c.case_number || "",
  case_type_id: c.case_type_id || "",
  court_location_id: c.court_location_id || "",
  plaintiff: c.plaintiff || "",
  defendant: c.defendant || "",
  in_re_case: !!c.in_re_case,
  in_re_case_name: c.in_re_case_name || "",
  judge: c.judge || "",
  date_filed: c.date_filed ? c.date_filed.slice(0, 10) : "",
  case_synopsis: c.case_synopsis || "",
});

export default function Cases() {
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);  // null = create mode

  const load = () => {
    api.get("/cases").then(r => setItems(r.data));
    api.get("/cases/types").then(r => setTypes(r.data));
    api.get("/locations").then(r => setLocations(r.data));
  };
  useEffect(() => { load(); }, []);

  const isEdit = !!editing;
  const f = useDrawerForm({ open: drawerOpen, isEdit, initialData: editing, emptyForm: EMPTY, hydrate });
  const { form, setField, busy, setBusy, err, setErr, requestClose } = f;

  const closeDrawer = () => { setDrawerOpen(false); setEditing(null); };

  const openCreate = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (c) => { setEditing(c); setDrawerOpen(true); };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const body = {
        case_number: form.case_number || null,
        case_type_id: form.case_type_id || null,
        court_location_id: form.court_location_id || null,
        plaintiff: form.plaintiff || null,
        defendant: form.defendant || null,
        in_re_case: !!form.in_re_case,
        in_re_case_name: form.in_re_case_name || null,
        judge: form.judge || null,
        date_filed: form.date_filed ? new Date(form.date_filed).toISOString() : null,
        case_synopsis: form.case_synopsis || null,
      };
      if (isEdit) await api.put(`/cases/${editing.id}`, body);
      else await api.post("/cases", body);
      closeDrawer();
      load();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const bulk = useBulkSelect(items, { testid: "case-row" });

  return (
    <Page
      title="Cases"
      subtitle="LEGAL / CASE FILE"
      breadcrumbs={[{ label: "Dashboard", to: "/" }, "Cases"]}
      actions={
        <button
          className="btn btn-primary"
          onClick={openCreate}
          data-testid="new-case-btn"
        >
          <Plus size={14} /> NEW CASE
        </button>
      }
      testid="cases-page"
    >
      <div className="surface">
        {items.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground" data-testid="empty-cases">
            <Scales size={32} weight="thin" className="mx-auto mb-3" />
            No cases yet. Add your first case to get started.
          </div>
        ) : (
          <table className="dense">
            <thead><tr>
              <th className="w-8"><input {...bulk.allProps()} /></th>
              <th>CASE #</th><th>TYPE</th><th>PLAINTIFF</th><th>DEFENDANT</th><th>JUDGE</th><th>FILED</th>
            </tr></thead>
            <tbody>
              {items.map(c => (
                <tr key={c.id} data-testid={`case-row-${c.id}`}>
                  <td><input {...bulk.rowProps(c.id)} /></td>
                  <td className="mono">
                    <button
                      className="link-cell"
                      onClick={() => openEdit(c)}
                      data-testid={`case-row-open-${c.id}`}
                      title="Open detailed view"
                    >
                      {c.case_number || c.id.slice(0, 8)}
                    </button>
                  </td>
                  <td>{types.find(t => t.id === c.case_type_id)?.name || "—"}</td>
                  <td>{c.plaintiff || "—"}</td>
                  <td>{c.defendant || "—"}</td>
                  <td>{c.judge || "—"}</td>
                  <td className="text-xs text-muted-foreground">
                    {c.date_filed ? new Date(c.date_filed).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Drawer
        open={drawerOpen}
        onClose={() => requestClose(closeDrawer)}
        title={isEdit ? "Edit case" : "New case"}
        size="narrow"
        testid="new-case-drawer"
        footer={
          <DrawerFooter
            testid="case-footer"
            onCancel={() => requestClose(closeDrawer)}
            onSave={save}
            mode={isEdit ? "edit" : "create"}
            busy={busy}
            saveLabel={isEdit ? "Update case" : "Create case"}
          />
        }
      >
        <ErrorBanner err={err} testid="case-form-error" />

        <Field label="CASE NUMBER">
          <input
            className="input"
            value={form.case_number}
            onChange={e => setField("case_number", e.target.value)}
            data-testid="case-number-input"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="CASE TYPE">
            <select
              className="input"
              value={form.case_type_id}
              onChange={e => setField("case_type_id", e.target.value)}
              data-testid="case-type-select"
            >
              <option value="">—</option>
              {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
          <Field label="COURT">
            <select
              className="input"
              value={form.court_location_id}
              onChange={e => setField("court_location_id", e.target.value)}
              data-testid="court-select"
            >
              <option value="">—</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="PLAINTIFF">
            <input
              className="input"
              value={form.plaintiff}
              onChange={e => setField("plaintiff", e.target.value)}
              data-testid="plaintiff-input"
            />
          </Field>
          <Field label="DEFENDANT">
            <input
              className="input"
              value={form.defendant}
              onChange={e => setField("defendant", e.target.value)}
              data-testid="defendant-input"
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="JUDGE">
            <input
              className="input"
              value={form.judge}
              onChange={e => setField("judge", e.target.value)}
            />
          </Field>
          <Field label="DATE FILED">
            <input
              type="date"
              className="input"
              value={form.date_filed}
              onChange={e => setField("date_filed", e.target.value)}
            />
          </Field>
        </div>

        <Field label="SYNOPSIS">
          <textarea
            className="input"
            rows={3}
            value={form.case_synopsis}
            onChange={e => setField("case_synopsis", e.target.value)}
          />
        </Field>
      </Drawer>
    </Page>
  );
}
